import { ColGroupDef } from '@ag-grid-community/core';
import {
  AgGridHelperService,
  ProductColGroupId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { SuppressDetails } from '@types';
import { FundListingPerformanceController } from '../controllers';

/**
 * Return performance column definitions for US SMA.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getPerformanceColumnDefsForUS_SMA(
  listingController: FundListingPerformanceController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getFavFundColDef(listingController),
        agGridHelperService.getFundNameColDef(
          null,
          null,
          listingController.productType,
          null,
          true // hide ticker on excel download,
        ),
      ],
    },
    {
      groupId: ProductColGroupId.ADDITIONAL_PERFORMANCE,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        headerLabelKey: 'products.total-returns',
        footnotePlacement: `${
          listingController.componentName
        }:${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}TotalReturnsPPSS`,
        asOfDateText: () => listingController.getPerformanceAsOf(),
      },
      children: [
        agGridHelperService.getCumulativeMonthly3MonthsColDef(
          listingController,
          suppressDetails,
          {
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
          }
        ),
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          listingController,
          suppressDetails,
          {
            headerName: translateService.instant(`products.ytd-total-returns`),
            headerComponentParams: {
              isAlignRight: true,
              footnotePlacement: `${
                listingController.componentName
              }:${agGridHelperService.getConfigurationNameForPlacement(
                listingController.productType
              )}YtdTotalReturnsPPSS`,
            },
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
          }
        ),
        agGridHelperService.getCumulativeQuarterly3MonthsColDef(
          listingController,
          suppressDetails,
          {
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
            width: 90,
          }
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          listingController,
          suppressDetails,
          {
            headerName: translateService.instant(`products.ytd-total-returns`),
            headerComponentParams: {
              isAlignRight: true,
              footnotePlacement: `${
                listingController.componentName
              }:${agGridHelperService.getConfigurationNameForPlacement(
                listingController.productType
              )}YtdTotalReturnsPPSS`,
            },
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
            width: 90,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly7YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly7YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getFundDocumentColDef(),
      ],
    },
  ];
  return colGroupDefs;
}
